<script>
/* eslint-disable vue/no-bare-strings-in-template */
import { mapGetters } from 'vuex';
import { useAlert, useTrack } from 'dashboard/composables';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import ReportFilterSelector from './components/FilterSelector.vue';
import { GROUP_BY_FILTER } from './constants';
import { REPORTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import ConversationsListTable from './components/ConversationsListTable.vue';
import ConversationFlowCard from './components/conversation/ConversationFlowCard.vue';
import ConversationLineChart from './components/conversation/ConversationLineChart.vue';
import ConversationMetricStack from './components/conversation/ConversationMetricStack.vue';
import OperationalEfficiencyCard from './components/conversation/OperationalEfficiencyCard.vue';
import ReportDownloadMenu from './components/ReportDownloadMenu.vue';
import { formatTime } from '@chatwoot/utils';

const REPORTS_KEYS = {
  CONVERSATIONS: 'conversations_count',
  CONVERSATIONS_WITH_INCOMING: 'conversations_with_incoming_messages_count',
  RESOLVED_CONVERSATIONS_WITH_INCOMING:
    'resolved_conversations_with_incoming_messages_count',
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  FIRST_RESPONSE_TIME: 'avg_first_response_time',
  RESOLUTION_TIME: 'avg_resolution_time',
  RESOLUTION_COUNT: 'resolutions_count',
  REPLY_TIME: 'reply_time',
};

export default {
  name: 'ConversationReports',
  components: {
    ReportFilterSelector,
    ConversationsListTable,
    ConversationFlowCard,
    ConversationLineChart,
    ConversationMetricStack,
    OperationalEfficiencyCard,
    ReportDownloadMenu,
  },
  data() {
    return {
      from: 0,
      to: 0,
      groupBy: GROUP_BY_FILTER[1],
      businessHours: false,
      conversationDirection: false,
      comunicanteCampaign: 'all',
      conversationsPageIndex: 0, // For conversations list pagination
      selectedConversationMetric: '',
      isExportingXlsx: false,
    };
  },
  computed: {
    ...mapGetters({
      accountSummary: 'getAccountSummary',
      accountReport: 'getAccountReports',
      summaryUIFlags: 'getReportSummaryUIFlags',
    }),
    metricCards() {
      return [
        {
          key: REPORTS_KEYS.CONVERSATIONS,
          label: this.$t('REPORT.METRICS.CONVERSATIONS.NAME'),
          value: this.formatMetric(REPORTS_KEYS.CONVERSATIONS),
          trend: this.calculateTrend(REPORTS_KEYS.CONVERSATIONS),
          icon: 'chat',
          accent: 'blue',
          isLoading: this.isSummaryLoading,
        },
        {
          key: REPORTS_KEYS.INCOMING_MESSAGES,
          label: this.$t('REPORT.METRICS.INCOMING_MESSAGES.NAME'),
          value: this.formatMetric(REPORTS_KEYS.INCOMING_MESSAGES),
          trend: this.calculateTrend(REPORTS_KEYS.INCOMING_MESSAGES),
          icon: 'arrow-download',
          accent: 'blue',
          isLoading: this.isSummaryLoading,
        },
        {
          key: REPORTS_KEYS.OUTGOING_MESSAGES,
          label: this.$t('REPORT.METRICS.OUTGOING_MESSAGES.NAME'),
          value: this.formatMetric(REPORTS_KEYS.OUTGOING_MESSAGES),
          trend: this.calculateTrend(REPORTS_KEYS.OUTGOING_MESSAGES),
          icon: 'send',
          accent: 'blue',
          isLoading: this.isSummaryLoading,
        },
      ];
    },
    flowDeltas() {
      return {
        incomingMessages: this.calculateTrend(REPORTS_KEYS.INCOMING_MESSAGES),
        conversations: this.calculateTrend(
          REPORTS_KEYS.CONVERSATIONS_WITH_INCOMING
        ),
        resolutions: this.calculateTrend(
          REPORTS_KEYS.RESOLVED_CONVERSATIONS_WITH_INCOMING
        ),
      };
    },
    efficiencyMetrics() {
      const previous = this.accountSummary.previous || {};
      return [
        {
          key: REPORTS_KEYS.FIRST_RESPONSE_TIME,
          label: this.$t('REPORT.METRICS.FIRST_RESPONSE_TIME.NAME'),
          value: this.accountSummary[REPORTS_KEYS.FIRST_RESPONSE_TIME],
          previous: previous[REPORTS_KEYS.FIRST_RESPONSE_TIME],
        },
        {
          key: REPORTS_KEYS.REPLY_TIME,
          label: this.$t('REPORT.METRICS.REPLY_TIME.NAME'),
          value: this.accountSummary[REPORTS_KEYS.REPLY_TIME],
          previous: previous[REPORTS_KEYS.REPLY_TIME],
        },
        {
          key: REPORTS_KEYS.RESOLUTION_TIME,
          label: this.$t('REPORT.METRICS.RESOLUTION_TIME.NAME'),
          value: this.accountSummary[REPORTS_KEYS.RESOLUTION_TIME],
          previous: previous[REPORTS_KEYS.RESOLUTION_TIME],
        },
      ];
    },
    conversationDashboardLabels() {
      return {
        flowTitle: 'Fluxo de Atendimento',
        conversationsWithIncoming: 'Conversas com entrada',
        resolvedConversations: 'Conversas com entrada resolvidas',
        averageMessagesPerConversation:
          'média de mensagens recebidas por conversa',
        resolutionRate: 'taxa de resolução',
        variation: 'variação',
        efficiencyTitle: 'Eficiência Operacional',
        comparison: 'Comparado ao período anterior',
        previous: 'anterior',
        withinTarget: 'Dentro da meta',
        attention: 'Atenção',
        evolutionTitle: 'Evolução diária',
      };
    },
    isFlowLoading() {
      return this.isSummaryLoading;
    },
    isEfficiencyLoading() {
      return this.isSummaryLoading;
    },
    isSummaryLoading() {
      return this.summaryUIFlags.isFetchingAccountSummary;
    },
    selectedConversationMetricLabel() {
      return this.metricCards.find(
        metric => metric.key === this.selectedConversationMetric
      )?.label;
    },
  },
  methods: {
    isMetricLoading(key) {
      return Boolean(this.accountReport?.isFetching?.[key]);
    },
    fetchAllData() {
      this.conversationsPageIndex = 0;
      this.fetchAccountSummary();
      this.fetchChartData();
      this.fetchConversationsList(0); // Fetch conversations on first page
    },
    fetchAccountSummary() {
      try {
        this.$store.dispatch('fetchAccountSummary', this.getRequestPayload());
      } catch {
        useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED'));
      }
    },
    fetchChartData() {
      [
        'CONVERSATIONS',
        'INCOMING_MESSAGES',
        'OUTGOING_MESSAGES',
        'FIRST_RESPONSE_TIME',
        'RESOLUTION_TIME',
        'RESOLUTION_COUNT',
        'REPLY_TIME',
      ].forEach(async key => {
        try {
          await this.$store.dispatch('fetchAccountReport', {
            metric: REPORTS_KEYS[key],
            ...this.getRequestPayload(),
          });
        } catch {
          useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        }
      });
    },
    getRequestPayload() {
      const {
        from,
        to,
        groupBy,
        businessHours,
        conversationDirection,
        comunicanteCampaign,
      } = this;
      return {
        from,
        to,
        groupBy: groupBy?.period,
        businessHours,
        conversationDirection,
        comunicanteCampaign,
      };
    },
    downloadAgentReports() {
      const { from, to, comunicanteCampaign } = this;
      const fileName = `agent-report-${format(
        fromUnixTime(to),
        'dd-MM-yyyy'
      )}.csv`;
      this.$store.dispatch('downloadAgentReports', {
        from,
        to,
        fileName,
        comunicanteCampaign,
      });
    },
    onFilterChange({
      from,
      to,
      groupBy,
      businessHours,
      selectedConversationDirection,
      selectedComunicanteCampaign,
    }) {
      this.from = from;
      this.to = to;
      this.groupBy = groupBy;
      this.businessHours = businessHours;
      this.conversationDirection = selectedConversationDirection.id;
      this.comunicanteCampaign = selectedComunicanteCampaign.id;
      this.fetchAllData();

      useTrack(REPORTS_EVENTS.FILTER_REPORT, {
        filterValue: {
          from,
          to,
          groupBy,
          businessHours,
          selectedConversationDirection,
          selectedComunicanteCampaign,
        },
        reportType: 'conversations',
      });
    },
    fetchConversationsList(page = 0) {
      const {
        from,
        to,
        groupBy,
        businessHours,
        conversationDirection,
        comunicanteCampaign,
      } = this;
      this.$store.dispatch('fetchConversationsList', {
        metric: this.selectedConversationMetric || REPORTS_KEYS.CONVERSATIONS,
        from,
        to,
        page: page + 1, // API uses 1-based pagination
        groupBy: groupBy?.period,
        businessHours,
        conversationDirection,
        comunicanteCampaign,
      });
    },
    onConversationsPageChange(pageIndex) {
      this.conversationsPageIndex = pageIndex;
      this.fetchConversationsList(pageIndex);
    },
    onConversationMetricSelect(metric) {
      const metricKey = metric?.key || '';
      const shouldClear = metricKey === this.selectedConversationMetric;
      this.selectedConversationMetric = shouldClear ? '' : metricKey;
      this.conversationsPageIndex = 0;
      this.fetchConversationsList(0);
    },
    async downloadConversationsXlsx() {
      if (this.isExportingXlsx) return;

      this.isExportingXlsx = true;
      try {
        await this.$store.dispatch('downloadConversationsListXlsx', {
          metric: this.selectedConversationMetric || REPORTS_KEYS.CONVERSATIONS,
          ...this.getRequestPayload(),
          fileName: 'conversas.xlsx',
        });
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      } finally {
        this.isExportingXlsx = false;
      }
    },
    calculateTrend(key) {
      const previousValue = this.accountSummary.previous?.[key];
      if (!previousValue) return 0;

      const currentValue = this.accountSummary[key] || 0;
      return Math.round(((currentValue - previousValue) / previousValue) * 100);
    },
    formatMetric(key, isTime = false) {
      const value = this.accountSummary[key] || 0;
      return isTime ? formatTime(value) : Number(value).toLocaleString();
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <ReportDownloadMenu
      class="button--fixed-top"
      :loading="isExportingXlsx"
      :xlsx-handler="downloadConversationsXlsx"
    />
    <ReportFilterSelector
      :show-agents-filter="false"
      show-group-by-filter
      @filter-change="onFilterChange"
    />
    <!-- prettier-ignore -->
    <div>
    <div class="grid grid-cols-1 gap-4 xl:grid-cols-4">
      <ConversationMetricStack
        :metrics="metricCards"
        :selected-metric="selectedConversationMetric"
        :loading-message="$t('REPORT.LOADING_CHART')"
        @metric-select="onConversationMetricSelect"
      />
      <ConversationLineChart
        class="xl:col-span-3"
        :title="conversationDashboardLabels.evolutionTitle"
        :empty-message="$t('REPORT.NO_ENOUGH_DATA')"
        :loading-message="$t('REPORT.LOADING_CHART')"
        :filters="getRequestPayload()"
      />
    </div>
    <div class="grid grid-cols-1 gap-4 mt-4 xl:grid-cols-2">
      <ConversationFlowCard
        :title="conversationDashboardLabels.flowTitle"
        :incoming-messages-label="$t('REPORT.METRICS.INCOMING_MESSAGES.NAME')"
        :conversations-label="
          conversationDashboardLabels.conversationsWithIncoming
        "
        :resolutions-label="conversationDashboardLabels.resolvedConversations"
        :average-messages-per-conversation-label="
          conversationDashboardLabels.averageMessagesPerConversation
        "
        :resolution-rate-label="conversationDashboardLabels.resolutionRate"
        :variation-label="conversationDashboardLabels.variation"
        :incoming-messages="accountSummary.incoming_messages_count"
        :conversations="
          accountSummary.conversations_with_incoming_messages_count
        "
        :resolutions="
          accountSummary.resolved_conversations_with_incoming_messages_count
        "
        :deltas="flowDeltas"
        :is-loading="isFlowLoading"
        :loading-message="$t('REPORT.LOADING_CHART')"
      />
      <OperationalEfficiencyCard
        :title="conversationDashboardLabels.efficiencyTitle"
        :comparison-label="conversationDashboardLabels.comparison"
        :previous-label="conversationDashboardLabels.previous"
        :within-target-label="conversationDashboardLabels.withinTarget"
        :attention-label="conversationDashboardLabels.attention"
        :variation-label="conversationDashboardLabels.variation"
        :metrics="efficiencyMetrics"
        :is-loading="isEfficiencyLoading"
        :loading-message="$t('REPORT.LOADING_CHART')"
      />
    </div>
    <!-- Conversations List Section -->
    <div class="mt-8">
      <div class="mb-4">
        <div>
          <h3 class="text-xl font-semibold text-slate-800 dark:text-slate-100">
            {{ $t('CONVERSATIONS_TABLE.HEADER.TITLE') || 'Conversas' }}
            <span v-if="selectedConversationMetricLabel">
              · {{ selectedConversationMetricLabel }}
            </span>
          </h3>
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{
              $t('CONVERSATIONS_TABLE.DESCRIPTION') ||
              'Lista de conversas baseada nos filtros selecionados'
            }}
          </p>
        </div>
      </div>
      <ConversationsListTable
        :page-index="conversationsPageIndex"
        @page-change="onConversationsPageChange"
      />
    </div>
    </div>
  </div>
</template>
